/**
 * AirfareSummaryRail — the summary card that leads with the WATCHED AIRFARE
 * (the figure TripMuscle actively tracks and can book), then shows the full
 * trip total with its pieces beneath. Per Wade: airfare is the focus; the total
 * trip price shows with its components.
 *
 * Honest by construction: airfare is the live estimate ("watched"); a line with
 * a confirmation reads "booked"; an unpriced line shows nothing fabricated. When
 * there are no add-ons the breakdown is just the single airfare line.
 */
import { formatPrice } from "@/components/trips/itinerary/itinerary-utils";
import type { CostBreakdown } from "@/lib/types";

interface Props {
  /** Combined airfare figure; null when unknown. Its MEANING is priceBasis:
   *  a watched quote, what was paid, or a part-booked mix. */
  airfare: number | null;
  breakdown?: CostBreakdown;
  travelers?: number | null;
  /** Where the airfare figure comes from (R1 × B4). Defaults to "quoted". */
  priceBasis?: "quoted" | "paid" | "mixed" | "held" | "none";
  /** Render without the outer card chrome (when embedded inside another card). */
  bare?: boolean;
  /** Phone only: the summary card above already states the figure, so hide it
   *  here and keep the breakdown. Laptop always shows it. */
  figureLaptopOnly?: boolean;
}

/** The eyebrow over the airfare figure. It must never call a paid price
 *  "watched" — the whole point of B4 is that those are different facts. */
const BASIS_EYEBROW: Record<string, string> = {
  quoted: "Airfare · watched 3×/day",
  paid: "Airfare · paid",
  mixed: "Airfare · part paid, part watched",
  held: "Airfare · on hold, quoted by the airline, not paid",
  none: "Airfare",
};

const STATUS_LABEL: Record<string, string> = {
  watched: "watched",
  booked: "paid",
  part_booked: "part paid",
  held: "on hold, not paid",
  planned: "planned",
};

const KIND_LABEL: Record<string, string> = {
  airfare: "Airfare",
  lodging: "Lodging",
  dining: "Dining",
  activity: "Activities",
  transfer: "Transport",
  event: "Event",
  note: "Note",
  other: "Other",
};

export default function AirfareSummaryRail({ airfare, breakdown, travelers, priceBasis = "quoted", bare, figureLaptopOnly }: Props) {
  const airfareLabel = formatPrice(airfare);
  if (!airfareLabel) return null;

  // Add-on lines are everything in the breakdown that isn't the airfare itself.
  const addOnLines = (breakdown?.lines ?? []).filter((l) => l.kind !== "airfare");
  const total = breakdown?.total ?? airfare;
  const totalLabel = formatPrice(total);
  const hasAddOns = addOnLines.length > 0;

  return (
    <div className={bare ? "" : "rounded-xl border bg-card p-4 shadow-paper"}>
      {/* Airfare — the focus */}
      <div className={figureLaptopOnly ? "hidden lg:block" : undefined}>
      <div
        className={
          "text-[11px] font-semibold uppercase tracking-wide " +
          (priceBasis === "paid" ? "text-emerald-700" : "text-muted-foreground")
        }
      >
        {BASIS_EYEBROW[priceBasis] ?? BASIS_EYEBROW.quoted}
      </div>
      <div className="mt-0.5 font-mono text-3xl font-bold tabular-nums text-foreground">{airfareLabel}</div>
      {travelers != null && travelers > 1 && (
        <div className="mt-0.5 text-xs text-muted-foreground">across {travelers} travelers</div>
      )}
      </div>

      {/* Trip total + pieces (only meaningful once there's more than airfare) */}
      {hasAddOns && totalLabel && (
        <div className="mt-3 border-t pt-3 text-sm">
          <div className="mb-1 flex items-center justify-between font-semibold">
            <span>Trip total</span>
            <span className="font-mono tabular-nums">{totalLabel}</span>
          </div>
          <div className="flex items-center justify-between py-0.5 text-muted-foreground">
            <span>
              {KIND_LABEL.airfare}{" "}
              <span className={"text-xs " + (priceBasis === "paid" ? "text-emerald-600" : "")}>
                {priceBasis === "paid" ? "paid" : priceBasis === "mixed" ? "part paid" : priceBasis === "held" ? "held, not paid" : "watched"}
              </span>
            </span>
            <span className="font-mono tabular-nums">{airfareLabel}</span>
          </div>
          {addOnLines.map((l, i) => (
            <div key={i} className="flex items-center justify-between py-0.5 text-muted-foreground">
              <span className="truncate">
                {l.label}{" "}
                {l.status === "booked" && (
                  <span className="text-xs text-emerald-600">{STATUS_LABEL.booked}</span>
                )}
                {l.status === "part_booked" && (
                  <span className="text-xs text-emerald-600">{STATUS_LABEL.part_booked}</span>
                )}
                {l.status === "planned" && <span className="text-xs">planned</span>}
              </span>
              <span className="font-mono tabular-nums">{formatPrice(l.cost)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
